import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { MimeType } from 'common/common/constants/mime-type.enum';
import { BoldRequest, ExcludeCsrfToken } from './csrf-token/csrf-token.service';

interface CustomConfig {
  skipHttpInterceptor?: boolean;
}

enum HttpMethod {
  GET,
  POST,
  PUT,
  DELETE
}

// Getting the type of config param from all supported methods, there's no named type for them
export type HttpRequestConfig = Parameters<typeof HttpClient.prototype.get>[1] &
  Parameters<typeof HttpClient.prototype.post>[2] &
  Parameters<typeof HttpClient.prototype.put>[2] &
  Parameters<typeof HttpClient.prototype.delete>[1] &
  CustomConfig;

@Injectable({
  providedIn: 'root'
})
export class BcApiService {
  constructor(private http: HttpClient) {
  }

  private _makeRequest(method: HttpMethod, uri: string, config: HttpRequestConfig, data?, excludeCsrfToken?: boolean): Promise<any> {
    config.context = config.context || new HttpContext();
    config.context.set(BoldRequest, true);

    if (excludeCsrfToken) {
      config.context.set(ExcludeCsrfToken, excludeCsrfToken);
    }

    config.withCredentials = true;

    switch (method) {
      case HttpMethod.GET:
        if (data) {
          config.params = new HttpParams({ fromObject: data });
        }
        return this.http.get(uri, config).toPromise();
      case HttpMethod.POST:
        return this.http.post(uri, data, config).toPromise();
      case HttpMethod.PUT:
        return this.http.put(uri, data, config).toPromise();
      case HttpMethod.DELETE:
        return this.http.delete(uri, config).toPromise();
      default:
        throw new Error('Unhandled request type: ' + method);
    }
  }

  private addDefaultHeaders(config: HttpRequestConfig): HttpRequestConfig {
    const newConfig: HttpRequestConfig = config || {};
    newConfig.headers = newConfig.headers || {};
    newConfig.headers['Content-Type'] = newConfig.headers['Content-Type'] || MimeType.APPLICATION_JSON;
    if (config) {
      if (config.skipHttpInterceptor) {
        // Workaround for passing metadata
        // https://github.com/angular/angular/issues/18155
        newConfig.headers['X-Skip-Interceptor'] = 'true';
      }
      delete config.skipHttpInterceptor;
    }
    return newConfig;
  }

  public get(uri, config: HttpRequestConfig, excludeCsrfToken?: boolean): Promise<any> {
    return this._makeRequest(HttpMethod.GET, uri, this.addDefaultHeaders(config), undefined, excludeCsrfToken);
  }

  public post(uri, data, config: HttpRequestConfig, excludeCsrfToken?: boolean): Promise<any> {
    return this._makeRequest(HttpMethod.POST, uri, this.addDefaultHeaders(config), data, excludeCsrfToken);
  }

  public put(uri, data, config: HttpRequestConfig, excludeCsrfToken?: boolean): Promise<any> {
    return this._makeRequest(HttpMethod.PUT, uri, this.addDefaultHeaders(config), data, excludeCsrfToken);
  }

  public delete(uri, config: HttpRequestConfig, excludeCsrfToken?: boolean): Promise<any> {
    return this._makeRequest(HttpMethod.DELETE, uri, this.addDefaultHeaders(config), undefined, excludeCsrfToken);
  }
}
